import React, { useEffect, useState } from "react";
import Modal from "./modal";
import MultiSelectSort from "./sortable-select";
import {
    getRecentApproverIds,
    sortApproversByRecentIds,
    updateRecentApproverIds,
} from "../utils/recent-approvers";

/**
 * ApproversDisplay component allows viewing and managing the approval workflow:
 * - Displays current approver, if any
 * - Allows reordering and editing of approver list
 * - Shows approval history logs
 */
const ApproversDisplay = ({
                              locked = false,
                              need_approval,
                              current_approver,
                              approvers_list,
                              users,
                              approval_history,
                              data,
                              cancelAction,
                              requestApproval,
                              saveApprovers,
                          }) => {
    const [approversList, setApproversList] = useState(approvers_list);
    const [availableApprovers, setAvailableApprovers] = useState([]);
    const clientId = data?.id_client;
    const username = data?.username;

    const isAnyOfGroup = (entry) => Array.isArray(entry);
    const flattenMembers = (list) => list.flatMap((entry) => isAnyOfGroup(entry) ? entry : [entry]);
    const hasAnyOfGroup = approversList.some(isAnyOfGroup);

    // Sort history descending by date
    const sortedHistory = [...approval_history].sort((a, b) =>
        new Date(b.updated_date) - new Date(a.updated_date)
    );

    // On mount, initialize availableApprovers
    useEffect(() => {
        filterAvailableApprovers(approvers_list);
    }, [users, approvers_list]);

    useEffect(() => {
        setApproversList(approvers_list);
    }, [approvers_list]);

    useEffect(() => {
        saveApprovers(approversList)
    }, [approversList])
    // Filter out already selected approvers
    const filterAvailableApprovers = (currentList) => {
        const excludeIds = new Set(flattenMembers(currentList).map((m) => m.id_user));
        const filtered = users.filter(u => !excludeIds.has(u.id_user));
        const recentApproverIds = getRecentApproverIds({ clientId, username });
        setAvailableApprovers(sortApproversByRecentIds(filtered, recentApproverIds));
    };

    // Map user object to react-select option
    const approverOption = user => ({
        value: user,
        label: `${user.first_name} ${user.last_name}`,
    });

    // Handlers
    const handleReorder = (newOptions) => {
        const newList = newOptions.map(opt => opt.value);
        updateRecentApproverIds({
            clientId,
            username,
            previousApproverIds: flattenMembers(approversList).map((approver) => approver.id_user),
            nextApproverIds: newList.map((approver) => approver.id_user),
        });
        setApproversList(newList);
        filterAvailableApprovers(newList);
    };

    // JSX render
    return (
        <Modal
            overflow="scroll"
            invalid={need_approval || approversList.length === 0 || locked}
            title={data.t.read("request_approval")}
            cancelAction={cancelAction}
            okAction={() => requestApproval(approversList)}
            okText={data.t.read("request_approval")}
            cancelText={data.t.read("close")}
        >
            {/* Current Approver / Any-of Head Section */}
            {need_approval && isAnyOfGroup(approversList[0]) && (
                <div className={"d-flex flex-column mb-4"}>
                    <span className={"py-2"}>{data.t.read("current_approver")}</span>
                    <div className={"card border-info"}>
                        <div className="card-body p-2">
                            <div className="mb-2">
                                <span className="badge badge-info mr-2 font-weight-bold">{data.t.read("invoice.approver_any_of_prefix")}</span>
                            </div>
                            <div>
                                {approversList[0].map((m, i) => {
                                    const name = `${m.first_name || ""} ${m.last_name || ""}`.trim();
                                    return name ? (
                                        <span key={i} className="badge badge-light border mr-1 mb-1">{name}</span>
                                    ) : null;
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {need_approval && !isAnyOfGroup(approversList[0]) && current_approver && current_approver?.id_user === approversList[0]?.id_user && (
                <div className={"d-flex flex-column mb-4"}>
                    <span className={"py-2"}> {data.t.read("current_approver")}</span>
                    <div className={"card "}>
                        <div className="card-body p-2">
                            <div className={"d-flex justify-content-between"}>
                                <span>{approverOption(current_approver).label}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Approver List Section — read-only when the workflow set an any-of group.
                Trailing sequential entries after the head group are also hidden here;
                edit them via the workflow builder rather than this manual list. */}
            {hasAnyOfGroup && (
                <div className="alert alert-info mb-4" role="status">
                    {data.t.read("approvers_display_any_of_locked_notice")}
                </div>
            )}
            {!hasAnyOfGroup && (
                <div className="mb-4">
                    <h5>{data.t.read("approver_list")}</h5>
                    <MultiSelectSort
                        options={availableApprovers.map(approverOption)}
                        value={approversList.map(approverOption)}
                        selected={approversList.map(approverOption)}
                        uneditableIndexes={(
                            need_approval &&
                            current_approver?.id_user != null &&
                            approversList?.[0]?.id_user != null &&
                            current_approver.id_user === approversList[0].id_user)
                            ? [0] : []}
                        setSelected={handleReorder}
                        locked={locked}
                        closeMenuOnSelect
                    />
                </div>
            )}

            {/* Approval History Section */}
            <div>
                <h5>{data.t.read("approval_history")}</h5>
                <table className="table table-striped">
                    <thead>
                    <tr>
                        <th>{data.t.read("approver")}</th>
                        <th>{data.t.read("statusTitle")}</th>
                        <th>{data.t.read("date")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedHistory.map((log, idx) => (
                        <tr key={idx} className="row-hover">
                            <td>{`${log?.approver?.first_name || ""} ${log?.approver?.last_name || ""}`}</td>
                            <td>{data.t.read(`status.${log.status}`)}</td>
                            <td>
                                {new Intl.DateTimeFormat(`${data.t.lang}-US`, {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false,
                                }).format(new Date(log.updated_date))}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </Modal>
    );
};

export default ApproversDisplay;
